import { Settings } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../AppContext";
import ClosedOrders from "../../components/ClosedOrders/ClosedOrders";
import CompanyPicker from "../../components/CompanyPicker/CompanyPicker";
import HelmetMeta from "../../components/HelmetMeta";
import PosCashCloseDialog from "../../components/PosCashCloseDialog/PosCashCloseDialog";
import SimpleFloorRooms from "../../components/SimpleFloorRooms";
import SimpleSpeedDialActions from "../../components/SimpleSpeedDialActions";
import TakeawayOrders from "../../components/TakeawayOrders/TakeawayOrders";
import config from "../../config";
import cayLogo from "../../images/logo.png";
import { useSiteSettings } from "../../shared/hooks/useFrappeDoctype";
import { useSearchPOSOpening } from "../../shared/hooks/usePOSData";
import { PosContext } from "../../shared/PosContext";
import { isDateBeforeToday } from "../../utils/dateUtils";
import { useStyles } from "../NewTables/styles";
import PosProfileDialog from "../Widgets/PosProfileDialog";
import ReservationDialog from "../Widgets/ReservationDialog";
import WidgetsDialogs from "../Widgets/WidgetsDialogs";
import Room from "./components/Room";
import SettingsDialog from "./components/SettingsDialog";
import PrintCashCloseReport from "../../components/PosCashCloseDialog/PrintCashCloseReport";

const Tables = () => {
    const { tablesMovingContext, posProfile, userInfo } =
        useContext(AppContext);

    const { data: siteSettings, isFetched } = useSiteSettings();
    const { refetch: refetchTodayOpen } = useSearchPOSOpening({
        user: userInfo.name,
    });

    const [showPrintDialog, setShowPrintDialog] = useState(false);
    const [reportData, setReportData] = useState(null);

    const logoUrl = useMemo(() => {
        if (siteSettings) {
            return siteSettings?.docs?.[0]?.logo
                ? config.baseUrl + siteSettings.docs[0].logo
                : cayLogo;
        } else {
            if (isFetched) {
                return cayLogo;
            }
        }
    }, [siteSettings]);

    const {
        changeSelectedTable,
        customerContext,
        selectedRoom,
        posEntryDetails,
        refetchTablesData,
        selectedTable,
    } = useContext(PosContext);

    const navigate = useNavigate();

    const classes = useStyles();

    const selectedOrder = useMemo(() => {
        if (selectedTable) {
            return Object.values(selectedTable.orders)[0];
        } else {
            return null;
        }
    }, [selectedTable]);

    const [selectedCustomer, setSelectedCustomer] = customerContext;

    const [moving, setMoving] = tablesMovingContext;
    const [settingsDialog, setSettingsDialog] = useState(false);

    const [widgetsDialogsOpen, setWidgetsDialogsOpen] = useState(false);
    const [widgetDialogTable, setWidgetDialogTable] = useState(null);

    const [takeawayOrdersOpen, setTakeawayOrdersOpen] = useState(false);

    const [closedOrdersOpen, setClosedOrdersOpen] = useState(false);
    const toggleClosedOrders = () => setClosedOrdersOpen(!closedOrdersOpen);

    const [closePOSDialgOpen, setClosePOSDialogOpen] = useState(false);
    const toggleClosePOSDialog = () =>
        setClosePOSDialogOpen(!closePOSDialgOpen);

    const [speedDialNotifications, setSpeedDialNotifications] = useState({});

    const [posProfileOpen, setPosProfileOpen] = useState(false);
    const togglePosProfile = () => setPosProfileOpen(!posProfileOpen);

    useEffect(() => {
        if (!posProfile) {
            setPosProfileOpen(true);
        } else {
            setPosProfileOpen(false);
        }
    }, [posProfile]);

    useEffect(() => {
        setSelectedCustomer(null);
        changeSelectedTable(null);
    }, []);

    useEffect(() => {
        refetchTablesData();
    }, []);

    useEffect(() => {
        if (
            posEntryDetails &&
            isDateBeforeToday(posEntryDetails.period_start_date)
        ) {
            setSpeedDialNotifications({
                openingEntries: 1,
            });
        }
    }, [posEntryDetails]);

    const handleOnChangeMove = (move) => {
        setMoving(move);
    };

    const onTableSelect = (table) => {
        navigate("/pos/" + table.tableDescription);
    };

    const handleInvoiceClosed = () => {
        setWidgetsDialogsOpen(false);
    };

    const handleCloseTable = (table) => {
        setWidgetDialogTable(table);
        setWidgetsDialogsOpen(true);
    };

    const handleCloseDialog = () => {
        setWidgetsDialogsOpen(false);
    };

    const handleTakeawayOrders = () => {
        setTakeawayOrdersOpen(true);
        changeSelectedTable("takeaway");
    };

    const handleClosedOrders = () => {
        toggleClosedOrders();
    };

    const handleClosePOSCash = () => {
        toggleClosePOSDialog();
    };

    const handleTakeawayOrdersClose = () => {
        setTakeawayOrdersOpen(false);
        changeSelectedTable(null);
    };

    return (
        <React.Fragment>
            <HelmetMeta title={"Tables"} />
            <div className={classes.PageWrap}>
                <div className={classes.NewTablesPage}>
                    <div className={classes.Header} id="Pos_Header">
                        <div className="Logo">
                            <img src={logoUrl} />
                        </div>
                        <CompanyPicker />
                        <div className={classes.RoomPicker}>
                            <SimpleFloorRooms />
                        </div>

                        <div className="SettingsIcon">
                            <IconButton
                                onClick={(e) => {
                                    setSettingsDialog(true);
                                }}
                            >
                                <Settings />
                            </IconButton>
                        </div>
                    </div>
                    <div className={classes.Body}>
                        <div className={classes.Body__TableView}>
                            <div className="TablesWrap">
                                <Room
                                    onTableSelect={onTableSelect}
                                    room={selectedRoom}
                                    moving={moving}
                                    handleOnChangeMove={handleOnChangeMove}
                                    handleTakeawayOrders={handleTakeawayOrders}
                                    handleCloseTableExt={handleCloseTable}
                                    speedDialNotifications={
                                        speedDialNotifications
                                    }
                                />
                            </div>
                        </div>
                        <SimpleSpeedDialActions
                            handleTakeawayOrders={handleTakeawayOrders}
                            handleClosedOrders={handleClosedOrders}
                            handleClosePOSCash={handleClosePOSCash}
                            notifications={speedDialNotifications}
                        />
                    </div>
                </div>
            </div>
            <WidgetsDialogs
                open={widgetsDialogsOpen}
                onClose={handleCloseDialog}
                table={widgetDialogTable}
                isTakeaway={false}
                onInvoiceClose={handleInvoiceClosed}
                order={selectedOrder}
            ></WidgetsDialogs>

            <TakeawayOrders
                open={takeawayOrdersOpen}
                onClose={handleTakeawayOrdersClose}
            />

            <ClosedOrders
                open={closedOrdersOpen}
                onClose={toggleClosedOrders}
            />

            <PosProfileDialog
                open={posProfileOpen}
                onClose={togglePosProfile}
            />

            <SettingsDialog open={settingsDialog} setOpen={setSettingsDialog} />
            <ReservationDialog />

            {closePOSDialgOpen && (
                <PosCashCloseDialog
                    open={closePOSDialgOpen}
                    onClose={toggleClosePOSDialog}
                    onSuccess={(res) => {
                        if (res) {
                            setReportData(res);
                            setShowPrintDialog(true);
                        }
                        refetchTodayOpen();
                    }}
                />
            )}

            {/* Print Cash Close Report Dialog */}
            {/* {showPrintDialog && reportData && (
                <PrintCashCloseReport
                    reportData={reportData}
                    onClose={() => {
                        setShowPrintDialog(false);
                    }}
                    onPrint={() => {
                        setShowPrintDialog(false);
                    }}
                />
            )} */}
        </React.Fragment>
    );
};

export default Tables;
